import { useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
} from '@react-pdf/renderer';
import parse from 'html-react-parser';
import ibmLogo from '../public/images/ibmlogo.png';
import { PDFData } from '../hooks/useData';
import { TranslationFunction } from 'src/common/hooks/widgets/useTranslation';

interface RadarData {
  product: string;
  feature: string;
  score: number;
}

interface pdfGenerateProps {
  pdfData?: PDFData | null;
  radarData: RadarData[];
  chartImage: string;
  scorePercentage: number;
  cratImageString: string;
  localeCode: string;
  useTranslation: TranslationFunction;
}

export const PDFGenerate = ({
  pdfData,
  radarData,
  chartImage,
  scorePercentage,
  cratImageString,
  useTranslation,
  localeCode,
}: pdfGenerateProps) => {
  Font.registerHyphenationCallback((word) => [word]);

  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#ffffff',
      paddingTop: 35,
      paddingBottom: 50,
      paddingHorizontal: 35,
      border: 'solid white 2px',
    },
    section: {
      fontSize: 12,
      textAlign: 'justify',
      lineHeight: 1.4,
    },
    title: { fontSize: 14, paddingBottom: 25, fontFamily: 'Helvetica-Bold' },
    score: { fontSize: 14, paddingBottom: 10, fontFamily: 'Helvetica-Bold' },
    subTitle: { paddingBottom: 25 },
    mb20: { marginBottom: 20 },
    mb10: { marginBottom: 10 },
    mb5: { marginBottom: 5 },
    image: { height: 300, width: 'auto' },
    subImage: {
      width: 30,
      height: 15,
      marginLeft: 50,
      position: 'absolute',
      fontSize: 12,
      bottom: 60,
      left: 0,
      right: 0,
      textAlign: 'left',
      color: 'grey',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 12,
      bottom: 30,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
    link: {
      color: 'blue',
      textDecoration: 'underline',
      paddingTop: 30,
    },
    radarImage: {
      width: 200,
      height: 260,
      paddingBottom: 30,
    },
    btnLink: {
      backgroundColor: '#0d63fe',
      color: '#FFFFFF',
      padding: 10,
      borderRadius: 5,
      textAlign: 'center',
      width: 200,
      marginTop: 30,
      textDecoration: 'none',
    },
    bold: {
      fontFamily: 'Helvetica-Bold',
    },
    italic: {
      fontFamily: 'Helvetica-Oblique',
    },
    boldItalic: {
      fontFamily: 'Helvetica-BoldOblique',
    },
  });

  const [currentDate, setCurrentDate] = useState<string>('');

  useEffect(() => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    const date = new Date();
    const swapLocale = (loc: string) => {
      const [a, b] = loc.split('-');
      return `${b.toLowerCase()}-${a.toUpperCase()}`;
    };
    const formattedDate = new Intl.DateTimeFormat(
      swapLocale(localeCode),
      options,
    ).format(date);
    setCurrentDate(formattedDate);
  }, []);

  const getRadarValue = (header: string) => {
    const filterArray = radarData.filter(
      (e) =>
        e.feature === header &&
        e.product == (useTranslation('chart_user', true) as string),
    );
    if (!!filterArray.length) return filterArray[0]['score'];
  };

  const mapHtmlToComponents = (html: string, boldItalic = false) => {
    html = replacePlaceholder(html);
    return parse(html, {
      replace: (domNode: any) => {
        if (domNode.type === 'tag') {
          switch (domNode.name) {
            case 'strong':
            case 'b':
              return (
                <Text style={boldItalic ? styles.boldItalic : styles.bold}>
                  {domNode.children.map((child: any) => child.data).join('')}
                </Text>
              );
            case 'br':
              return <Text>{'\n'}</Text>;
            default:
              return (
                <Text>
                  {domNode.children.map((child: any) => child.data).join('')}
                </Text>
              );
          }
        }
        if (domNode.type === 'text') return <Text>{domNode.data}</Text>;
      },
    });
  };

  const replacePlaceholder = (text: string): string => {
    return text.replaceAll(/&lt;/g, '<').replace(/&gt;/g, '>');
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page1?.heading}</Text>
          <Text style={styles.subTitle}>{currentDate}</Text>
          <Image style={styles.image} src={cratImageString} />
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page2?.heading}</Text>
          {pdfData?.page2?.desc
            ? mapHtmlToComponents(pdfData?.page2?.desc)
            : ''}
          <Link style={styles.link} src={pdfData?.page2?.link || ''}>
            {pdfData?.page2?.link}
          </Link>
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.title}>{pdfData?.page3?.heading}</Text>
          <Image style={styles.radarImage} src={chartImage} />
          {pdfData?.page3?.sections?.map((v, i) => (
            <View key={i}>
              <Text
                style={{
                  padding: 10,
                  fontSize: 14,
                  marginBottom: 10,
                  backgroundColor: v.color,
                  fontFamily: 'Helvetica-Bold',
                }}
              >
                {v.heading}
              </Text>
              <Text style={styles.score}>
                {v.scoreLabel}: {getRadarValue(v.heading)}%
              </Text>
              <Text style={styles.subTitle}>
                {v.desc ? mapHtmlToComponents(v.desc) : ''}
              </Text>
            </View>
          ))}
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text
            style={{
              fontSize: 14,
              padding: 10,
              marginBottom: 30,
              backgroundColor: pdfData?.page5?.color,
              fontFamily: 'Helvetica-Bold',
            }}
          >
            {pdfData?.page5?.heading}
          </Text>
          <Text style={styles.title}>
            {pdfData?.page5?.scoreLabel}: {scorePercentage}%
          </Text>
          <Text style={styles.title}>{pdfData?.page5?.scoreKeyLabel}</Text>
          {pdfData?.page5?.scoreLimit.map((v, i) => (
            <View key={i}>
              <Text style={styles.mb10}>{v.label}</Text>
              <Text style={styles.mb20}>{v.desc}</Text>
            </View>
          ))}
          <View style={styles.italic}>
            {pdfData?.page5?.footnote
              ? mapHtmlToComponents(pdfData?.page5?.footnote, true)
              : ''}
          </View>
          <Link style={styles.btnLink} src={pdfData?.page5?.footNoteLink || ''}>
            {pdfData?.page5?.footNoteBtnLabel}
          </Link>
        </View>
        <Image style={styles.subImage} src={ibmLogo} />
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
